import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardBody,
  Chip,
  Spinner,
  Button,
  Select,
  SelectItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Divider,
  Tooltip,
  Tabs,
  Tab,
} from '@nextui-org/react';
import { RangeCalendar } from '@nextui-org/calendar';
import type { DateValue } from '@internationalized/date';
import type { RangeValue } from '@react-types/shared';
import { Icon } from '@iconify/react';
import { formatDistanceToNow } from 'date-fns';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useGetDatamodelsMetricsQuery, useGetDatamodelsQuery, type DatamodelMetricRun } from '@circuitry-ai/doc-data';
import { ConvertToLocalDate, useAgGridStatePersistence, AnalystChart, type AnalystChartConfig, type AnalystChartType } from '@circuitry-ai/doc-ui';

const DAYS_OPTIONS = [
  { labelKey: 'Last 7 Days', value: '7' },
  { labelKey: 'Last 15 Days', value: '15' },
  { labelKey: 'Last 30 Days', value: '30' },
  { labelKey: 'Custom', value: 'custom' },
] as const;

type DaysOptionValue = (typeof DAYS_OPTIONS)[number]['value'];

function formatDateParam(dateValue: DateValue): string {
  const year = dateValue.year;
  const month = String(dateValue.month).padStart(2, '0');
  const day = String(dateValue.day).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function mbToGb(mb: number): number {
  return mb / 1024;
}

function formatGb(value: number): string {
  const gb = mbToGb(value);
  if (gb >= 1) return `${gb.toFixed(2)}GB`;
  return `${value.toFixed(2)}MB`;
}

export default function SchemaMetricsView() {
  const { t } = useTranslation();
  const { schemaId } = useParams<{ schemaId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');
  const category = categoryParam ?? schemaId ?? '';

  const [selectedTimePeriod, setSelectedTimePeriod] = useState<DaysOptionValue>('7');
  const [customDateRange, setCustomDateRange] = useState<RangeValue<DateValue> | null>(null);
  const [isCustomPopoverOpen, setIsCustomPopoverOpen] = useState(false);
  const metricsQueryParams = useMemo(() => {
    if (selectedTimePeriod === 'custom') {
      if (customDateRange?.start && customDateRange?.end) {
        return {
          category,
          start_date: formatDateParam(customDateRange.start),
          end_date: formatDateParam(customDateRange.end),
        };
      }
      return {
        category,
      };
    }

    return {
      category,
      days: Number(selectedTimePeriod),
    };
  }, [category, customDateRange, selectedTimePeriod]);
  const shouldSkipMetricsQuery =
    !category ||
    (selectedTimePeriod === 'custom' &&
      (!customDateRange?.start || !customDateRange?.end));
  const {
    data: metricsResponse,
    isLoading: isLoadingMetrics,
    isFetching: isFetchingMetrics,
    isError: isMetricsError,
  } = useGetDatamodelsMetricsQuery(
    metricsQueryParams,
    { skip: shouldSkipMetricsQuery }
  );

  const isMetricsLoading = isLoadingMetrics || isFetchingMetrics;

  const { data: datamodelsResponse } = useGetDatamodelsQuery(undefined, { skip: !schemaId });

  const schemaName = useMemo(() => {
    if (!schemaId || !datamodelsResponse?.data?.schemas) return schemaId ?? t('Metrics');
    const schema = datamodelsResponse.data.schemas.find((s) => s.id === schemaId);
    return schema?.name ?? schemaId;
  }, [schemaId, datamodelsResponse, t]);

  const metrics = metricsResponse?.data?.metrics ?? [];
  const aggregatedRuns = useMemo(() => {
    const byRun = new Map<string, DatamodelMetricRun>();
    for (const row of metrics) {
      const existing = byRun.get(row.pipeline_run_id);
      if (existing) {
        existing.records_processed += row.records_processed ?? 0;
        existing.records_parsed_size_MB += row.records_parsed_size_MB ?? 0;
        existing.data_quality_issues += row.data_quality_issues ?? 0;
        existing.schema_drift_events += row.schema_drift_events ?? 0;
      } else {
        byRun.set(row.pipeline_run_id, { ...row });
      }
    }
    return Array.from(byRun.values()).sort(
      (a, b) => new Date(b.pipeline_trigger_time).getTime() - new Date(a.pipeline_trigger_time).getTime()
    );
  }, [metrics]);

  const latestRun = aggregatedRuns[0] ?? null;
  const previousRun = aggregatedRuns[1] ?? null;

  const claimsPctVsPrevious = useMemo(() => {
    if (!latestRun || !previousRun || previousRun.records_processed === 0) return null;
    const pct = ((latestRun.records_processed - previousRun.records_processed) / previousRun.records_processed) * 100;
    return pct;
  }, [latestRun, previousRun]);

  const qualityScorePctChange = useMemo(() => {
    if (!latestRun || !previousRun) return null;
    const current = latestRun.schema_compliance ?? 0;
    const previous = previousRun.schema_compliance ?? 0;
    const diff = current - previous;
    if (diff === 0) return 0;
    return diff;
  }, [latestRun, previousRun]);

  const volumePctVsAvg = useMemo(() => {
    if (!latestRun || aggregatedRuns.length === 0) return null;
    const sum = aggregatedRuns.reduce((a, r) => a + (r.records_parsed_size_MB ?? 0), 0);
    const avg = sum / aggregatedRuns.length;
    if (avg === 0) return null;
    const pct = ((latestRun.records_parsed_size_MB - avg) / avg) * 100;
    return pct;
  }, [latestRun, aggregatedRuns]);

  const businessTotals = latestRun
    ? {
        total_claims_ingested: latestRun.total_claims_ingested,
        total_quality_issues: latestRun.total_quality_issues,
        total_schema_drifts: latestRun.total_schema_drifts,
      }
    : null;

  if (!schemaId) {
    return (
      <div className="flex items-center justify-center py-16 text-danger">
        {t('Missing schema ID')}
      </div>
    );
  }

  if (!category) {
    return (
      <div className="flex items-center justify-center py-16 text-default-500">
        {t('Missing category. Open metrics from Data page.')}
      </div>
    );
  }

  if (isMetricsLoading && metrics.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <Spinner size="lg" color="primary" />
        <p className="text-default-500 font-medium">{t('Loading metrics...')}</p>
      </div>
    );
  }

  if (isMetricsError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-2 text-danger">
        <Icon icon="material-symbols:error-outline" width={48} />
        <p>{t('Failed to load metrics. Try again.')}</p>
        <Button color="primary" variant="flat" onPress={() => navigate('/data')}>
          {t('Back to Data')}
        </Button>
      </div>
    );
  }

  if (metrics.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <Icon icon="solar:database-outline" width={64} className="text-default-300" />
        <p className="text-default-500 font-medium">{t('No Data available')}</p>
        <Button color="primary" variant="flat" onPress={() => navigate('/data')}>
          {t('Back to Data')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Header: single line - title, status, scheduled chip (primary), last updated, breadcrumb */}
      <div className="px-6 py-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3 min-w-0">
          <h1 className="text-foreground font-sfui font-semibold text-2xl leading-tight shrink-0">
            {t('Pipeline Observability')}
          </h1>
          <Chip size="sm" color="primary" variant="flat">
            {t('Scheduled')}: {t('Every Day')}
          </Chip>
          {latestRun?.pipeline_trigger_time && (
            <span className="text-default-500 text-sm shrink-0">
              {t('Last updated')}: {formatDistanceToNow(new Date(latestRun.pipeline_trigger_time), { addSuffix: true })}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <TimePeriodPicker
            selectedTimePeriod={selectedTimePeriod}
            setSelectedTimePeriod={setSelectedTimePeriod}
            customDateRange={customDateRange}
            setCustomDateRange={setCustomDateRange}
            isCustomPopoverOpen={isCustomPopoverOpen}
            setIsCustomPopoverOpen={setIsCustomPopoverOpen}
            isLoading={isMetricsLoading}
          />
          <div className="text-sm text-default-500">
            <button type="button" onClick={() => navigate('/data')} className="hover:text-foreground">
              {t('Data')}
            </button>
            <span className="mx-2">/</span>
            <span className="text-foreground font-medium">{schemaName}</span>
          </div>
        </div>
      </div>

      <Divider />

      {/* Top row: 5 KPI cards with icons and percentage/secondary info */}
      <div className="px-6 pb-4 pt-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <KpiCard
            title={t('Latest Pipeline Run Date')}
            value={latestRun ? ConvertToLocalDate(latestRun.pipeline_trigger_time) : '—'}
            detail={
              latestRun?.pipeline_trigger_time
                ? t('{{relative}} last execution', {
                    relative: formatDistanceToNow(new Date(latestRun.pipeline_trigger_time), { addSuffix: true }),
                  })
                : undefined
            }
            sparkline={
              <KpiIconBadge color="primary">
                <Icon icon="solar:calendar-minimalistic-bold-duotone" width={34} height={34} />
                {latestRun?.pipeline_trigger_time && (
                  <span className="absolute left-1/2 top-[58%] -translate-x-1/2 -translate-y-1/2 text-[10px] font-bold leading-none">
                    {new Date(latestRun.pipeline_trigger_time).getDate()}
                  </span>
                )}
              </KpiIconBadge>
            }
          />
          <KpiCard
            title={t('Total Records Processed ')}
            value={businessTotals ? businessTotals.total_claims_ingested.toLocaleString() : '—'}
            detail={
              <span className="inline-flex items-center gap-1 ">
                <span className="inline-flex items-center justify-center rounded-full p-1 bg-default-100">
                  <Icon icon="mdi:clock-outline" width={12} height={12} />
                </span>
                {t('for the selected period')}
              </span>
            }
            sparkline={
              <KpiIconBadge color="warning">
                <Icon icon="mdi:file-document-outline" width={34} height={34} />
              </KpiIconBadge>
            }
          />
          <KpiCard
            title={t('Data Processed in Last Run')}
            value={latestRun ? latestRun.records_processed.toLocaleString() : '—'}
            detailNumber={
              claimsPctVsPrevious != null
                ? (claimsPctVsPrevious >= 0 ? `+${claimsPctVsPrevious.toFixed(1)}` : claimsPctVsPrevious.toFixed(1)) + '%'
                : undefined
            }
            detailSuffix={claimsPctVsPrevious != null ? t('vs previous run') : undefined}
            sparkline={
              <KpiIconBadge color="primary">
                <Icon icon="solar:database-bold-duotone" width={34} height={34} />
              </KpiIconBadge>
            }
          />
          <KpiCard
            title={t('Data Quality Score')}
            value={latestRun != null && latestRun.schema_compliance != null ? `${latestRun.schema_compliance.toFixed(1)}%` : '—'}
            detailNumber={
              qualityScorePctChange != null
                ? (qualityScorePctChange >= 0 ? `+${qualityScorePctChange.toFixed(1)}` : qualityScorePctChange.toFixed(1)) + '%'
                : undefined
            }
            detailSuffix={qualityScorePctChange != null ? t('last run quality') : undefined}
            sparkline={
              <KpiIconBadge color="success">
                <Icon icon="solar:check-circle-linear" width={34} height={34} />
              </KpiIconBadge>
            }
          />
          <KpiCard
            title={t('Data Volume Processed')}
            value={latestRun ? formatGb(latestRun.records_parsed_size_MB) : '—'}
            detailNumber={
              volumePctVsAvg != null
                ? (volumePctVsAvg >= 0 ? `+${volumePctVsAvg.toFixed(1)}` : volumePctVsAvg.toFixed(1)) + '%'
                : undefined
            }
            detailSuffix={volumePctVsAvg != null ? t('vs avg run') : undefined}
            sparkline={
              <KpiIconBadge color="success">
                <Icon icon="tabler:stack-2" width={34} height={34} />
              </KpiIconBadge>
            }
          />
        </div>
      </div>

      {/* Ingestion Trend + Pipeline Runs – two side-by-side sections */}
      <div className="px-6 pb-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="border border-default-200 shadow-sm">
            <CardBody className="pt-4">
              <IngestionTrendChart metrics={metrics} />
            </CardBody>
          </Card>
          <Card className="border border-default-200 shadow-sm">
            <CardHeader className="pb-2">
              <span className="text-base font-semibold text-foreground">{t('Pipeline Runs')}</span>
            </CardHeader>
            <CardBody className="pt-0">
              <RecentEtlRunsGrid metrics={metrics} t={t} />
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}

/**
 * Time-period selector (7 / 15 / 30 days / custom range). Rendered in the page
 * header; selecting "Custom" opens a range-calendar popover. Behaviour is
 * unchanged from the previous standalone section — only its position moved.
 */
function TimePeriodPicker({
  selectedTimePeriod,
  setSelectedTimePeriod,
  customDateRange,
  setCustomDateRange,
  isCustomPopoverOpen,
  setIsCustomPopoverOpen,
  isLoading,
}: Readonly<{
  selectedTimePeriod: DaysOptionValue;
  setSelectedTimePeriod: (value: DaysOptionValue) => void;
  customDateRange: RangeValue<DateValue> | null;
  setCustomDateRange: (value: RangeValue<DateValue> | null) => void;
  isCustomPopoverOpen: boolean;
  setIsCustomPopoverOpen: (open: boolean) => void;
  isLoading: boolean;
}>) {
  const { t } = useTranslation();
  return (
    <Popover
      isOpen={selectedTimePeriod === 'custom' && isCustomPopoverOpen}
      onOpenChange={setIsCustomPopoverOpen}
      placement="bottom-end"
      showArrow
      backdrop="transparent"
    >
      <PopoverTrigger>
        <div
          className="w-[170px] shrink-0"
          onClickCapture={() => {
            if (selectedTimePeriod === 'custom' && !isLoading) {
              setIsCustomPopoverOpen(true);
            }
          }}
        >
          <Select
            aria-label={t('Time period')}
            selectedKeys={[selectedTimePeriod]}
            onSelectionChange={(keys) => {
              const selectedKey = Array.from(keys)[0];
              if (selectedKey == null) return;
              const nextValue = String(selectedKey) as DaysOptionValue;
              setSelectedTimePeriod(nextValue);
              if (nextValue === 'custom') {
                setIsCustomPopoverOpen(true);
                return;
              }
              setIsCustomPopoverOpen(false);
              setCustomDateRange(null);
            }}
            size="sm"
            className="w-full"
            variant="bordered"
            isLoading={isLoading}
            isDisabled={isLoading}
          >
            {DAYS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </SelectItem>
            ))}
          </Select>
        </div>
      </PopoverTrigger>
      <PopoverContent className="border-none bg-transparent p-0 shadow-none">
        <div className="relative rounded-medium bg-content1">
          <button
            type="button"
            className="absolute right-1 z-10 inline-flex h-6 w-6 items-center justify-center rounded-md text-default-500 hover:bg-default-100"
            aria-label={t('Close date picker')}
            onClick={() => setIsCustomPopoverOpen(false)}
          >
            <Icon icon="material-symbols:close-rounded" width={16} />
          </button>
          <RangeCalendar
            aria-label={t('Custom date range')}
            value={customDateRange}
            onChange={(nextRange) => {
              setCustomDateRange(nextRange);
              if (nextRange?.start && nextRange?.end) {
                setIsCustomPopoverOpen(false);
              }
            }}
            visibleMonths={1}
            isDisabled={isLoading}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}

function StatusChip({ status }: Readonly<{ status: string }>) {
  const { t } = useTranslation();
  const color =
    status === 'Succeeded' ? 'success' : status === 'Failed' ? 'danger' : 'warning';
  return (
    <Chip size="sm" color={color} variant="flat">
      {t(status)}
    </Chip>
  );
}

function StatusCellRenderer(props: Readonly<{ value?: string }>) {
  const { value } = props;
  if (!value) return <span>—</span>;
  return <StatusChip status={value} />;
}

/** RUN ID cell: truncated id + copy icon; tooltip shows "Copied!" after copy. */
function RunIdCellRenderer(props: Readonly<{ value?: string }>) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const runId = props.value ?? '';
  const displayText = runId ? `${runId.slice(0, 8)}…` : '—';

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!runId) return;
    try {
      await navigator.clipboard.writeText(runId);
      setCopied(true);
      globalThis.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  if (!runId) return <span>—</span>;

  return (
    <div className="flex items-center gap-2 w-full min-w-0 h-full min-h-0 py-0 leading-none">
      <span className="font-mono text-sm truncate flex-1 min-w-0 leading-none" title={runId}>
        {displayText}
      </span>
      <span className="inline-flex shrink-0">
        <Tooltip content={copied ? t('Copied!') : t('Copy run ID')} placement="top" showArrow>
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center justify-center w-7 h-7 min-w-7 rounded-md hover:bg-default-100 text-default-400 hover:text-default-600 transition-colors"
            aria-label={t('Copy run ID')}
          >
            <Icon icon="hugeicons:copy-01" width={14} height={14} />
          </button>
        </Tooltip>
      </span>
    </div>
  );
}

/** Ag Grid: RUN ID, Run Time, Status, Records Processed, Quality Issues, Schema Issues. No pagination. */
function RecentEtlRunsGrid({
  metrics,
  t,
}: Readonly<{
  metrics: DatamodelMetricRun[];
  t: (key: string, options?: Record<string, unknown>) => string;
}>) {
  const columnDefs = useMemo<ColDef<DatamodelMetricRun>[]>(
    () => [
      {
        field: 'pipeline_run_id',
        headerName: t('RUN ID'),
        flex: 1,
        minWidth: 140,
        cellRenderer: RunIdCellRenderer,
        cellClass: 'ag-cell-run-id',
      },
      {
        field: 'pipeline_trigger_time',
        headerName: t('Run Time'),
        flex: 1,
        minWidth: 160,
        valueFormatter: (params) =>
          params.value ? ConvertToLocalDate(params.value) : '—',
      },
      {
        field: 'status',
        headerName: t('Status'),
        flex: 1,
        minWidth: 100,
        cellRenderer: StatusCellRenderer,
      },
      {
        field: 'records_processed',
        headerName: t('Records Processed'),
        flex: 1,
        minWidth: 120,
        valueFormatter: (params) =>
          params.value != null ? Number(params.value).toLocaleString() : '—',
      },
      {
        field: 'data_quality_issues',
        headerName: t('Quality Issues'),
        flex: 1,
        minWidth: 110,
        valueFormatter: (params) =>
          params.value != null ? String(params.value) : '—',
      },
      {
        field: 'schema_drift_events',
        headerName: t('Schema Issues'),
        flex: 1,
        minWidth: 110,
        valueFormatter: (params) =>
          params.value != null ? String(params.value) : '—',
      },
    ],
    [t]
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
    }),
    []
  );

  const { augmentGridReady, mergeColumnDefs, suppressDragLeaveHidesColumns } = useAgGridStatePersistence('schema-metrics-view-table');
  const columnDefsWithSort = useMemo(() => mergeColumnDefs(columnDefs), [columnDefs, mergeColumnDefs]);
  const onGridReady = useCallback(
    (params: { api: any }) => {
      augmentGridReady(params, (p) => {
        p?.api?.sizeColumnsToFit?.();
      });
    },
    [augmentGridReady]
  );

  if (metrics.length === 0) {
    return (
      <p className="text-sm text-default-500 py-6 text-center">{t('No runs in this period.')}</p>
    );
  }

  return (
    <div
      className="ag-theme-alpine"
      style={{ width: '100%', height: 360 }}
    >
      <AgGridReact<DatamodelMetricRun>
        suppressDragLeaveHidesColumns={suppressDragLeaveHidesColumns}
        rowData={metrics}
        columnDefs={columnDefsWithSort}
        defaultColDef={defaultColDef}
        getRowId={(params) => params.data.pipeline_run_id}
        suppressPaginationPanel
        pagination={false}
        rowHeight={42}
        onGridReady={onGridReady}
      />
    </div>
  );
}

/** Standalone colored icon symbol shown on the right of a KPI card. */
function KpiIconBadge({
  color,
  children,
}: Readonly<{
  color: 'primary' | 'success' | 'warning';
  children: React.ReactNode;
}>) {
  const colorClass = color === 'success' ? 'text-success' : color === 'warning' ? 'text-warning' : 'text-primary';
  return (
    <span className={`relative flex shrink-0 items-center justify-center ${colorClass}`} aria-hidden>
      {children}
    </span>
  );
}

function KpiCard({
  title,
  value,
  detail,
  detailNumber,
  detailSuffix,
  sparkline,
}: Readonly<{
  title: string;
  value: string;
  detail?: React.ReactNode;
  /** Percentage number only (e.g. "+8.2%" or "-100.0%") – colored green if positive, danger if negative */
  detailNumber?: string;
  /** Text after the number (e.g. " vs previous run") – no color */
  detailSuffix?: string;
  /** Decorative mini chart rendered in the top-right of the card. */
  sparkline?: React.ReactNode;
}>) {
  const isNegativeNumber = detailNumber != null && detailNumber.startsWith('-');
  const numberColorClass = detailNumber != null ? (isNegativeNumber ? 'text-danger font-medium' : 'text-success font-medium') : '';
  const arrowBgClass = isNegativeNumber ? 'bg-danger/10' : 'bg-success/10';

  return (
    <Card className="border border-default-200 shadow-sm">
      <CardBody className="flex flex-row items-center justify-between gap-3">
        <div className="flex flex-col min-w-0 gap-2">
          <span className="text-xs font-medium text-default-500 uppercase tracking-wide">{title}</span>
          <p className="text-lg sm:text-xl font-semibold text-foreground mt-1 truncate">{value}</p>
          {detail != null && (
            <p className="text-xs mt-1 text-default-500">{detail}</p>
          )}
          {detailNumber != null && (
            <p className="text-xs mt-1 text-default-500 flex flex-wrap items-center gap-1">
              <span className={`inline-flex items-center gap-1 ${numberColorClass}`}>
                <span className={`inline-flex items-center justify-center rounded-full p-0.5 ${arrowBgClass}`}>
                  <Icon icon={isNegativeNumber ? 'solar:alt-arrow-down-bold' : 'solar:alt-arrow-up-bold'} width={12} height={12} />
                </span>
                {detailNumber}
              </span>
              {detailSuffix ? <span>{detailSuffix}</span> : null}
            </p>
          )}
        </div>
        {sparkline && <div className="flex shrink-0 items-center">{sparkline}</div>}
      </CardBody>
    </Card>
  );
}

const TREND_VIEW_OPTIONS: { key: AnalystChartType; labelKey: string }[] = [
  { key: 'line', labelKey: 'Line' },
  { key: 'bar', labelKey: 'Bar' },
  { key: 'step', labelKey: 'Step' },
];

/**
 * Ingestion Trend chart (records processed per run over time). Renders through
 * the shared agentic `AnalystChart` so it inherits the compact K/M/B/T axis
 * scaling, non-clipping margins, dark-mode palette and export menu. Users can
 * switch between line / bar / step views.
 */
function IngestionTrendChart({ metrics }: Readonly<{ metrics: DatamodelMetricRun[] }>) {
  const { t } = useTranslation();
  const [view, setView] = useState<AnalystChartType>('line');

  // The API returns one row per table (and per run) — multiple rows can share
  // the same calendar day. Aggregate them into a single point per day so the
  // x-axis shows one label per date with the summed records processed.
  const data = useMemo(() => {
    const byDay = new Map<string, { date: string; claims: number; sortKey: number }>();
    for (const run of metrics) {
      const triggered = new Date(run.pipeline_trigger_time);
      const dayKey = `${triggered.getFullYear()}-${triggered.getMonth()}-${triggered.getDate()}`;
      const existing = byDay.get(dayKey);
      const claims = run.records_processed ?? 0;
      if (existing) {
        existing.claims += claims;
      } else {
        byDay.set(dayKey, {
          date: triggered.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
          claims,
          sortKey: new Date(triggered.getFullYear(), triggered.getMonth(), triggered.getDate()).getTime(),
        });
      }
    }
    return Array.from(byDay.values())
      .sort((a, b) => a.sortKey - b.sortKey)
      .map(({ date, claims }) => ({ date, claims }));
  }, [metrics]);

  const config = useMemo<AnalystChartConfig>(
    () => ({
      chartType: view,
      xField: 'date',
      yField: 'claims',
      yLabel: t('Claims'),
      colors: ['chart-primary'],
      data,
    }),
    [view, data, t]
  );

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
          <Icon icon="solar:chart-square-bold" width={18} className="text-primary" aria-hidden />
          {t('Ingestion Trend')}
        </h3>
        <Tabs
          size="sm"
          radius="lg"
          aria-label={t('Chart view')}
          selectedKey={view}
          onSelectionChange={(key) => setView(key as AnalystChartType)}
        >
          {TREND_VIEW_OPTIONS.map((option) => (
            <Tab key={option.key} title={t(option.labelKey)} />
          ))}
        </Tabs>
      </div>
      {data.length === 0 ? (
        <p className="text-sm text-default-500 py-4">{t('No ingestion data to display.')}</p>
      ) : (
        <AnalystChart config={config} height={260} />
      )}
    </div>
  );
}

